import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import {
  update_project_detail_dates,
  update_project_dates,
  update_detail_and_project_dates,
  delete_project_date,
} from './graphql/dealDates';
import { Apollo } from 'apollo-angular';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component';
import Group from '../../../interfaces/group';
import { combineLatest, Subscription } from 'rxjs';
import { DealDatesService } from './deal-dates.service';
import ProjectDetail from '../../../interfaces/project-detail';

const UPDATE_PROJECT_DETAIL_DATES = update_project_detail_dates;
const UPDATE_PROJECT_DATES = update_project_dates;
const UPDATE_DETAIL_AND_PROJECT_DATES = update_detail_and_project_dates;
const DELETE_PROJECT_DATE = delete_project_date;
export interface DealDateTable {
  dateTypeID: string;
  dateType: string;
  name: string;
  date: string;
  newdate: string;
  hasPermission: boolean;
  dateid: string;
  isTarget: boolean;
  newIsTarget: boolean;
  permissions?: {
    _create: boolean;
    _edit: boolean;
    _view: boolean;
    _delete: boolean;
  };
}

@Component({
    selector: 'deal-dates',
    templateUrl: './deal-dates.component.html',
    styleUrls: ['./deal-dates.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DealDatesComponent implements OnInit {
  @Input() dates: any;
  @Input() loi: string | undefined;
  @Input() effective: string | undefined;
  @Input() projID: string;
  @Input() projDetailID: string;
  @Input() statuses: any;
  @Input() permissions: any;
  @Input() actualDateClass: any;
  @Input() targetDateClass: any;
  @Input() dateTypes: any;
  @Input() projectDetail: any;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['name', 'date'];
  dataSource: MatTableDataSource<DealDateTable>;
  showHeader = false;
  flagLOIExpiration = false;
  isEditMode: boolean = false;
  hasEditPermissions: boolean = false;
  projectDates: any;
  availableDates: any;
  dealDatesSub: Subscription;

  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    private dialog: MatDialog,
    public dealDatesService: DealDatesService
  ) {}

  ngOnInit() {
    this.dealDatesService.setDates(this.dates);
    this.dealDatesService.setDetails(
      this.projectDetail as unknown as ProjectDetail
    );
    let effectivePerm = false;
    for (let i = 0; i < this.permissions.groups.length; i++) {
      if (
        this.permissions.groups[i].name === 'Information Technology' ||
        this.permissions.groups[i].name === 'Administration'
      ) {
        effectivePerm = true;
        break;
      }
    }

    if (
      this.permissions.projectDetailPermissions._edit ||
      this.permissions.dateTypePermissions._edit ||
      this.permissions.proposalDeadlineDateTypePermissions._edit ||
      this.permissions.fullClosedDateTypePermissions._edit ||
      this.permissions.capitalReapprovedDateTypePermissions._edit ||
      effectivePerm
    ) {
      this.hasEditPermissions = true;
    }
    this.dealDatesSub = combineLatest({
      dates: this.dealDatesService.dates$,
      projectDetail: this.dealDatesService.projectDetail$,
    }).subscribe(({ dates, projectDetail }) => {
      this.dates = dates;
      this.loi = projectDetail?.loi_expiration_date;
      this.effective = projectDetail?.effective_closing_date;

      this.projectDates = this.formatDealDates();
      this.availableDates = this.formatAvailableDates();
      this.dataSource = new MatTableDataSource<DealDateTable>(
        this.projectDates
      );
    });
  }
  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  formatDealDates() {
    let data: Array<DealDateTable> = [];
    if (this.loi) {
      let projDate = moment.utc(this.loi).format('MM/DD/YYYY');

      data.push({
        dateTypeID: 'loi_expiration_date',
        dateType: 'loi',
        name: 'LOI Expiration Date',
        date: moment.utc(this.loi).format('MM/DD/YYYY'),
        hasPermission: this.hasDatePermission('LOI Expiration Date'),
        isTarget: false,
        newIsTarget: false,
        dateid: this.projDetailID,
        newdate: new Date(projDate).toISOString(),
      });

      //Determine if the LOI Expiration needs to be flagged
      if (this.statuses && this.statuses.length > 0) {
        for (let i = 0; i < this.statuses.length; i++) {
          //If the Closing Status Sequence is active on the deal
          if (
            this.statuses[i]['statusSequence']['name'] === 'Closing' &&
            this.statuses[i].active
          ) {
            let indexLOI = 0;
            let indexStatus = 0;
            for (
              let j = 0;
              j < this.statuses[i]['statusSequence']['status_sequence_details'];
              j++
            ) {
              if (
                this.statuses[i]['statusSequence']['status_sequence_details'][
                  j
                ]['status']['name'] === 'Signed LOI'
              ) {
                indexLOI =
                  this.statuses[i]['statusSequence']['status_sequence_details'][
                    j
                  ]['index'];
              } else if (
                this.statuses[i]['statusSequence']['status_sequence_details'][
                  j
                ]['status']['name'] === this.statuses[i]['status']['name']
              ) {
                indexStatus =
                  this.statuses[i]['statusSequence']['status_sequence_details'][
                    j
                  ]['index'];
              }
            }

            //If the Closing status is on or before Signed LOI
            //and the current date is greater than or equal to the LOI expiration date, flag the warning as true
            this.flagLOIExpiration =
              indexStatus <= indexLOI &&
              moment().isSameOrAfter(this.loi, 'day');
          }
        }
      }
    }

    if (this.effective) {
      let projDate = moment.utc(this.effective).format('MM/DD/YYYY');

      data.push({
        dateTypeID: 'effective_closing_date',
        dateType: 'effective',
        name: 'Effective Closing Date',
        date: moment.utc(this.effective).format('MM/DD/YYYY'),
        hasPermission: this.hasDatePermission('Effective Closing Date'),
        isTarget: false,
        newIsTarget: false,
        dateid: this.projDetailID,
        newdate: new Date(projDate).toISOString(),
      });
    }

    if (this.dates && this.dates.length > 0) {
      for (let i = 0; i < this.dates.length; i++) {
        if (
          this.dates[i]['dateType'].active &&
          (this.dates[i]['dateType'].model === 'projects' ||
            this.dates[i]['dateType'].model === 'diligence')
        ) {
          let projDate = moment.utc(this.dates[i]['date']).format('MM/DD/YYYY');

          data.push({
            dateTypeID: this.dates[i]['dateType']['id'],
            dateType: this.dates[i]['dateType']['model'],
            name: this.dates[i]['dateType']['name'],
            hasPermission: this.hasDatePermission(
              this.dates[i]['dateType']['name']
            ),
            date: projDate,
            dateid: this.dates[i].id,
            newdate: new Date(projDate).toISOString(),
            isTarget: this.dates[i]['dateClass']['name'] === 'target',
            newIsTarget: this.dates[i]['dateClass']['name'] === 'target',
            permissions: this.dates[i]['permissions'],
          });
        }
      }
    }

    return data.sort((a, b) => (a.name > b.name ? 1 : -1));
  }

  formatAvailableDates() {
    let addAvailableDates = [];

    if (!this.loi && this.hasDatePermission('LOI Expiration Date')) {
      addAvailableDates.push({
        id: this.projDetailID,
        name: 'LOI Expiration Date',
      });
    }

    if (!this.effective && this.hasDatePermission('Effective Closing Date')) {
      addAvailableDates.push({
        id: this.projDetailID,
        name: 'Effective Closing Date',
      });
    }

    for (let i = 0; i < this.dateTypes.length; i++) {
      if (this.dateTypes[i].model === 'projects') {
        if (this.projectDates.length > 0) {
          let hasDate = false;
          for (let j = 0; j < this.projectDates.length; j++) {
            if (this.projectDates[j].name === this.dateTypes[i].name) {
              hasDate = true;
              break;
            }
          }

          if (
            !hasDate &&
            this.hasDatePermission(this.dateTypes[i].name, 'add')
          ) {
            addAvailableDates.push({
              id: this.dateTypes[i].id,
              name: this.dateTypes[i].name,
            });
          }
        } else if (this.hasDatePermission(this.dateTypes[i].name, 'add')) {
          addAvailableDates.push({
            id: this.dateTypes[i].id,
            name: this.dateTypes[i].name,
          });
        }
      }
    }
    addAvailableDates.sort((a, b) => (a.name > b.name ? 1 : -1));
    return addAvailableDates;
  }
  checkDateSpecificPermission(dateTypeName: string): boolean {
    return !!this.dateTypes.find(
      (dateType: any) => dateType.name === dateTypeName
    )?.permissions._edit;
  }
  hasDatePermission(dateType: string, action?: string): boolean {
    let effectivePerm = false;
    for (let i = 0; i < this.permissions.groups.length; i++) {
      if (
        this.permissions.groups[i].name === 'Information Technology' ||
        this.permissions.groups[i].name === 'Administration'
      ) {
        effectivePerm = true;
        break;
      }
    }

    if (
      this.permissions.projectDetailPermissions._edit &&
      this.permissions.dateTypePermissions._edit &&
      dateType !== 'LOI Closing Date' &&
      effectivePerm
    ) {
      return true;
    }

    if (dateType === 'LOI Expiration Date') {
      return (
        this.permissions.groups.filter(
          (group: Group) =>
            group.name === 'Information Technology' ||
            group.name === 'Acquisitions Administration'
        ).length > 0
      );
    } else if (dateType === 'Effective Closing Date') {
      return effectivePerm;
    } else if (dateType === 'Proposal Deadline') {
      return this.permissions.proposalDeadlineDateTypePermissions._edit;
    } else if (dateType === 'Full Closed') {
      return this.permissions.fullClosedDateTypePermissions._edit;
    } else if (dateType === 'Capital Re-Approved') {
      return this.permissions.capitalReapprovedDateTypePermissions._edit;
    } else if (dateType === 'LOI Closing Date' || dateType === 'LOI Maturity') {
      return (
        this.checkDateSpecificPermission(dateType) ||
        (action === 'add' &&
          this.permissions.groupsFlattened.find(
            (group: any) => group.name === 'Acquisitions'
          ))
      );
    }

    return false;
  }

  changeDate(event: any, i: number) {
    if (event.value !== '' && event.value !== null) {
      this.projectDates[i].newdate = new Date(
        moment.utc(event.value).format('MM/DD/YYYY')
      ).toISOString();
    } else {
      this.projectDates[i].newdate = '';
    }
  }

  saveDates() {
    if (this.isEditMode) {
      let datesChanged = [];
      let loi = '';
      let effective = '';
      let details = {};

      for (let i = 0; i < this.projectDates.length; i++) {
        let newdate = moment.utc(this.projectDates[i].newdate);

        if (
          this.projectDates[i].date !== newdate.format('MM/DD/YYYY') ||
          this.projectDates[i].isTarget !== this.projectDates[i].newIsTarget ||
          this.projectDates[i].dateid === null
        ) {
          if (this.projectDates[i].name === 'LOI Expiration Date') {
            loi = newdate.format('YYYY-MM-DD');
          } else if (this.projectDates[i].name === 'Effective Closing Date') {
            effective = newdate.format('YYYY-MM-DD');
          } else {
            if (this.projectDates[i].dateid) {
              datesChanged.push({
                id: this.projectDates[i].dateid,
                date: newdate.format('YYYY-MM-DD'),
                date_class_id: this.projectDates[i].newIsTarget
                  ? this.targetDateClass.id
                  : this.actualDateClass.id,
              });
            } else {
              datesChanged.push({
                date: newdate.format('YYYY-MM-DD'),
                date_class_id: this.projectDates[i].newIsTarget
                  ? this.targetDateClass.id
                  : this.actualDateClass.id,
                date_type_id: this.projectDates[i].dateTypeID,
              });
            }
          }
        }
      }
      if (
        loi !== '' &&
        effective !== '' &&
        this.hasDatePermission('Effective Closing Date')
      ) {
        details = {
          id: this.projDetailID,
          loi_expiration_date: loi,
          effective_closing_date: effective,
        };
      } else if (loi !== '') {
        details = {
          id: this.projDetailID,
          loi_expiration_date: loi,
        };
      } else if (
        effective !== '' &&
        this.hasDatePermission('Effective Closing Date')
      ) {
        details = {
          id: this.projDetailID,
          effective_closing_date: effective,
        };
      }

      if (datesChanged.length > 0 && Object.keys(details).length !== 0) {
        this.apollo
          .mutate({
            mutation: UPDATE_DETAIL_AND_PROJECT_DATES,
            variables: {
              detailInput: details,
              input: {
                id: this.projID,
                projectDates: {
                  upsert: datesChanged,
                },
              },
            },
          })
          .subscribe((response: any) => {
            let { data } = response;
            let dateResults =
              data.updateProjectDates.projectDates[0].project.projectDates;
            let detailResults = data.updateProjectDetailDates;

            this.dates = dateResults;
            this.loi = detailResults.loi_expiration_date;
            this.effective = detailResults.effective_closing_date;

            this.projectDates = this.formatDealDates();
            this.dataSource = new MatTableDataSource<DealDateTable>(
              this.projectDates
            );

            this.openSnackBar('Updated Deal Dates.');
          });
      } else if (datesChanged.length > 0) {
        this.apollo
          .mutate({
            mutation: UPDATE_PROJECT_DATES,
            variables: {
              input: {
                id: this.projID,
                projectDates: {
                  upsert: datesChanged,
                },
              },
            },
          })
          .subscribe((response: any) => {
            //@ts-ignore
            let { data } = response;
            this.dates =
              data.updateProjectDates.projectDates[0].project.projectDates;

            this.projectDates = this.formatDealDates();
            this.dataSource = new MatTableDataSource<DealDateTable>(
              this.projectDates
            );

            this.openSnackBar('Updated Deal Dates.');
          });
      } else if (Object.keys(details).length !== 0) {
        this.apollo
          .mutate({
            mutation: UPDATE_PROJECT_DETAIL_DATES,
            variables: {
              input: details,
            },
          })
          .subscribe(({ data }) => {
            // @ts-ignore
            let results = data.updateProjectDetailDates;

            this.loi = results.loi_expiration_date;
            this.effective = results.effective_closing_date;
            this.projectDates = this.formatDealDates();
            this.dataSource = new MatTableDataSource<DealDateTable>(
              this.projectDates
            );

            this.openSnackBar('Updated Deal Dates.');
          });
      }
    }
    this.isEditMode = !this.isEditMode;
  }

  deleteDate(row: any, index: number) {
    if (row.dateid !== null) {
      let info = '';
      if (
        row.name === 'LOI Expiration Date' ||
        row.name === 'Effective Closing Date'
      ) {
        info = row.name;
      } else {
        info = row.isTarget ? 'Target ' + row.name : 'Actual ' + row.name;
      }

      const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
        ConfirmationModalComponent,
        {
          width: '600px',
          height: '380px',
          data: {
            modalTitle: 'Delete Deal Date',
            actionMessage: 'Are you sure you want to delete the Deal Date?',
            modalInformation: [
              {
                title: 'Date Type',
                info: info,
              },
              {
                title: 'Date',
                info: row.date,
              },
            ],
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        }
      );
      ref.afterClosed().subscribe((data) => {
        if (data.clicked === 'Ok') {
          if (row.name === 'LOI Expiration Date') {
            this.apollo
              .mutate({
                mutation: UPDATE_PROJECT_DETAIL_DATES,
                variables: {
                  input: {
                    id: row.dateid,
                    loi_expiration_date: null,
                  },
                },
              })
              .subscribe(({ data }) => {
                // @ts-ignore

                this.loi = '';
                this.projectDates.splice(index, 1);
                this.dataSource = new MatTableDataSource<DealDateTable>(
                  this.projectDates
                );
                this.availableDates = this.formatAvailableDates();

                this.openSnackBar('Deleted LOI Expiration Date.');
              });
          } else if (row.name === 'Effective Closing Date') {
            this.apollo
              .mutate({
                mutation: UPDATE_PROJECT_DETAIL_DATES,
                variables: {
                  input: {
                    id: row.dateid,
                    effective_closing_date: null,
                  },
                },
              })
              .subscribe(({ data }) => {
                // @ts-ignore

                this.effective = '';
                this.projectDates.splice(index, 1);
                this.dataSource = new MatTableDataSource<DealDateTable>(
                  this.projectDates
                );
                this.availableDates = this.formatAvailableDates();

                this.openSnackBar('Deleted Effective Closing Date.');
              });
          } else {
            this.apollo
              .mutate({
                mutation: DELETE_PROJECT_DATE,
                variables: {
                  id: row.dateid,
                },
              })
              .subscribe(({ data }) => {
                // @ts-ignore
                this.dates = data.deleteProjectDate.project.projectDates;
                this.projectDates.splice(index, 1);
                this.dataSource = new MatTableDataSource<DealDateTable>(
                  this.projectDates
                );
                this.availableDates = this.formatAvailableDates();

                this.openSnackBar('Deleted Deal Date.');
              });
          }
        }
      });
    } else {
      this.projectDates.splice(index, 1);
      if (row.name === 'LOI Expiration Date') {
        this.availableDates.push({
          id: 'loi_expiration_date',
          name: row.name,
        });
      } else if (row.name === 'Effective Closing Date') {
        this.availableDates.push({
          id: 'effective_closing_date',
          name: row.name,
        });
      } else {
        this.availableDates.push({
          id: row.dateTypeID,
          name: row.name,
        });
      }
      this.dataSource = new MatTableDataSource<DealDateTable>(
        this.projectDates
      );
    }
  }

  addDate(date: any, i: number) {
    this.projectDates.push({
      dateTypeID: date.id,
      name: date.name,
      date: moment.utc().format('MM/DD/YYYY'),
      isTarget: false,
      hasPermission: this.hasDatePermission(date.name, 'add'),
      newIsTarget: false,
      dateid: null,
      newdate: new Date().toISOString(),
    });
    this.dataSource = new MatTableDataSource<DealDateTable>(this.projectDates);
    this.availableDates.splice(i, 1);
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }

  checkDates(): boolean {
    if (this.projectDates.length > 0) {
      for (let i = 0; i < this.projectDates.length; i++) {
        if (!this.checkDate(this.projectDates[i].newdate)) {
          return false;
        }
      }
    }
    return true;
  }

  checkDate(date: string): boolean {
    if (date === '') {
      return false;
    }
    return moment.isDate(new Date(date));
  }
}
